import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Globe,
  KeyRound,
  Loader2,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { RowActions } from "@/components/ui/RowActions";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { NewVaultEntryDrawer } from "../components/NewVaultEntryDrawer";
import {
  useDeleteVaultEntry,
  useVaultCategories,
  useVaultList,
  useVaultSensitivities,
  useVaultStats,
} from "../hooks/useVault";
import type { VaultListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

const CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  database: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  server_ssh: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)" },
  api_token: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)" },
  cloud_service: { bg: "hsl(180,55%,92%)", fg: "hsl(180,72%,28%)" },
  domain_dns: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
  email_service: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  payment_gateway: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  dev_tools: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)" },
  social_media: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  external_link: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
  other: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

const SENSITIVITY_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  low: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Low" },
  medium: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)", label: "Medium" },
  high: { bg: "hsl(22,90%,92%)", fg: "hsl(22,90%,38%)", label: "High" },
  critical: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)", label: "Critical" },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function timeAgo(iso: string | null): string {
  if (!iso) return "Never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CategoryBadge({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  const c = CATEGORY_COLORS[value] ?? CATEGORY_COLORS.other;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.fg }}
    >
      {label}
    </span>
  );
}

function SensitivityBadge({ value }: { value: string }) {
  const s = SENSITIVITY_COLORS[value] ?? SENSITIVITY_COLORS.medium;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function StatCard({
  label,
  value,
  isAlert,
}: {
  label: string;
  value: number;
  isAlert?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: BORDER }}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-semibold ${
          isAlert && value > 0 ? "text-[hsl(0,70%,40%)]" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function CredentialIcons({ entry }: { entry: VaultListItem }) {
  const items: { icon: string; label: string; color: string }[] = [];
  if (entry.hasPassword)
    items.push({ icon: "🔑", label: "password", color: "hsl(0,70%,45%)" });
  if (entry.hasApiKey)
    items.push({ icon: "🗝", label: "API key", color: "hsl(262,83%,45%)" });
  if (entry.hasToken)
    items.push({ icon: "🎫", label: "token", color: "hsl(38,90%,40%)" });
  if (entry.hasUsername)
    items.push({ icon: "👤", label: "username", color: "hsl(210,100%,40%)" });
  if (entry.hasHost)
    items.push({ icon: "🌐", label: "host", color: "hsl(180,72%,30%)" });
  if (entry.isExternal && entry.externalUrl)
    items.push({ icon: "🔗", label: "URL", color: "hsl(215,16%,47%)" });
  if (items.length === 0)
    return <span className="text-xs text-muted-foreground">—</span>;
  const tooltip = `Has ${items.map((i) => i.label).join(", ")}`;
  return (
    <span className="inline-flex items-center gap-0.5" title={tooltip}>
      {items.map((it, idx) => (
        <span key={idx} className="text-[12px]">
          {it.icon}
        </span>
      ))}
    </span>
  );
}

function ExpiryCell({ expiry }: { expiry: string | null }) {
  if (!expiry) return <span className="text-muted-foreground">—</span>;
  const date = new Date(expiry);
  const diffMs = date.getTime() - Date.now();
  const days = Math.floor(diffMs / (24 * 3600 * 1000));
  const expired = days < 0;
  const expiringSoon = !expired && days <= 30;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span
        style={{
          color: expired || expiringSoon ? "hsl(0,70%,40%)" : "hsl(215,16%,40%)",
          fontWeight: expired || expiringSoon ? 500 : 400,
        }}
      >
        {formatDate(expiry)}
      </span>
      {expired && (
        <span
          className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          style={{ background: "hsl(0,70%,94%)", color: "hsl(0,70%,40%)" }}
        >
          Expired
        </span>
      )}
      {expiringSoon && (
        <span
          className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
          style={{ background: "hsl(38,90%,94%)", color: "hsl(35,90%,32%)" }}
        >
          Expiring soon
        </span>
      )}
    </div>
  );
}

export default function VaultListPage() {
  const navigate = useNavigate();
  const { data: stats } = useVaultStats();
  const { data: categories = [] } = useVaultCategories();
  const { data: sensitivities = [] } = useVaultSensitivities();
  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
  });
  const { data: projectsResult } = useProjectList({ page: 1, limit: 200 });
  const companies = companiesResult?.data ?? [];
  const projects = projectsResult?.data ?? [];

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sensitivity, setSensitivity] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search, category, sensitivity, companyId, projectId, expiringOnly]);

  const params = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      category: category || undefined,
      sensitivity: sensitivity || undefined,
      companyId: companyId || undefined,
      projectId: projectId || undefined,
      expiringDays: expiringOnly ? 30 : undefined,
    }),
    [page, limit, search, category, sensitivity, companyId, projectId, expiringOnly],
  );

  const { data, isLoading, isError } = useVaultList(params);
  const deleteEntry = useDeleteVaultEntry();

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;
  const items = data?.data ?? [];
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  function handleDelete(entry: VaultListItem) {
    setDeleteTarget({ id: entry.id, name: entry.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteEntry.mutateAsync(deleteTarget.id);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Resources" },
        { label: "Access Vault" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KeyRound
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">
              Access Vault
            </h1>
          </div>
          <Button onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            New Entry
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard label="Total Entries" value={stats?.total ?? 0} />
          <StatCard
            label="Expiring Soon"
            value={stats?.expiringSoon ?? 0}
            isAlert
          />
          <StatCard
            label="Critical Entries"
            value={stats?.critical ?? 0}
            isAlert
          />
          <StatCard label="Accessed Today" value={stats?.accessedToday ?? 0} />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[280px]">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search vault…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-44"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value)}
            className="w-40"
          >
            <option value="">All Sensitivity</option>
            {sensitivities.map((s) => (
              <option key={s.id} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-44"
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-44"
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => setExpiringOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-xs font-medium ${
              expiringOnly
                ? "bg-[hsl(0,70%,96%)] text-[hsl(0,70%,40%)]"
                : "bg-white text-muted-foreground"
            }`}
            style={{ borderColor: BORDER }}
            title="Show only entries expiring within 30 days"
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
            Expiring Soon
          </button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Column settings"
            className="ml-auto"
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>

        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: BORDER }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
              Loading vault…
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-sm text-destructive">
              Failed to load vault entries.
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No entries match the current filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead
                className="text-left text-xs font-medium uppercase tracking-wide text-muted-foreground"
                style={{ background: "hsl(210,30%,97%)" }}
              >
                <tr>
                  <th className="px-4 py-3 w-24">#</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Sensitivity</th>
                  <th className="px-4 py-3">Entity</th>
                  <th className="px-4 py-3">Credentials</th>
                  <th className="px-4 py-3">Expiry</th>
                  <th className="px-4 py-3">Last Accessed</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((entry) => {
                  const catLabel =
                    categories.find((c) => c.value === entry.category)?.label ??
                    entry.category;
                  return (
                    <tr
                      key={entry.id}
                      className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                      onClick={() => navigate(`/access-vault/${entry.id}`)}
                    >
                      <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                        {entry.code}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">
                        {entry.title}
                        {entry.isExternal && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]"
                            style={{
                              background: "hsl(210,16%,93%)",
                              color: "hsl(215,16%,40%)",
                            }}
                          >
                            <Globe className="h-2.5 w-2.5" strokeWidth={1.5} />
                            External
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <CategoryBadge
                          value={entry.category}
                          label={catLabel}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <SensitivityBadge value={entry.sensitivity} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {entry.companyName || entry.productName || entry.projectName ? (
                          <div className="flex flex-col">
                            {entry.companyName && (
                              <span>{entry.companyName}</span>
                            )}
                            {entry.projectName && (
                              <span className="text-[10px]">
                                {entry.projectName}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <CredentialIcons entry={entry} />
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <ExpiryCell expiry={entry.expiryDate} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {entry.lastAccessedAt ? (
                          <div className="flex items-center gap-1.5">
                            <span>{timeAgo(entry.lastAccessedAt)}</span>
                            {entry.lastAccessedByName && (
                              <span className="text-[11px]">
                                by {entry.lastAccessedByName}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span>Never</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActions
                          onView={() =>
                            navigate(`/access-vault/${entry.id}`)
                          }
                          onEdit={() =>
                            navigate(`/access-vault/${entry.id}?edit=true`)
                          }
                          onDelete={() => handleDelete(entry)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "9px 14px",
              borderTop: `1px solid ${BORDER}`,
              background: "hsl(210,30%,97%)",
              fontSize: "12px",
              color: "hsl(215,16%,47%)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>
                Showing {startItem}-{endItem} of {total} entries
              </span>
              <span style={{ marginLeft: "8px" }}>Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  height: "26px",
                  padding: "0 4px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  fontSize: "12px",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  width: "28px",
                  height: "28px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  background: "white",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  opacity: page <= 1 ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ‹
              </button>
              <span style={{ padding: "0 10px" }}>
                {totalPages === 0 ? "0" : page} / {totalPages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  width: "28px",
                  height: "28px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "4px",
                  background: "white",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  opacity: page >= totalPages ? 0.4 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      <NewVaultEntryDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete vault entry?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Access Vault.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
